import { useQueryClient } from '@tanstack/react-query'; // Import useQueryClient
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { axiosInstance } from '../../config/axios'; // Import configured axios instance
import { useNavHeight } from '../Context/NavHeightContext';
import { useAuth } from '../../hooks/useAuth'; // Added useAuth
import useFetch from '../../hooks/useFetch';
import { useStickyFlip } from '../../hooks/useStickyFlip'; // Import custom hook
import { useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	FullPageLoader,
	LoadingGif,
	PageTitle,
	WideContentContainer,
	StickyFlipPaginationContainer,
	ContentWrapper,
} from '../Shared/CommonStyles';
import { Button } from '../Shared/FormStyles';
import Pagination from '../Shared/Pagination';

import AlbumGrid from './AlbumGrid';
import * as S from './GalleryContainer.styles';
import Pictures, {
	type PendingChange,
	type PendingChangesMap,
} from './Pictures';
import ClassificarDialog from './ClassificarDialog';
import ClassificationResults from './ClassificationResults';
import YearSuggestDialog from './YearSuggestDialog';
import CategoryGrid from './CategoryGrid'; // Import CategoryGrid

const generateSchoolYears = () => {
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth(); // 0-11
	const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
	const years = [];
	for (let y = startYear; y >= 2000; y--) {
		years.push(`${y}-${y + 1}`);
	}
	return years;
};

const schoolYearsList = generateSchoolYears();
// Reversed list for slider (oldest year first, so left is older, right is newer)
const schoolYearsForSlider = [...schoolYearsList].reverse();

const GalleryContainer = () => {
	const { categoryId, albumId } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuth(); // Get user from auth context
	const { unitats, isFetchingUnitats, fetchUnitatsError } = useUnitats(); // Keep unitats fetch for mapping names if needed
	const queryClient = useQueryClient(); // Get query client instance
	const { navHeight } = useNavHeight();

	// Active category can be null (Category Grid view)
	const [activeCategory, setActiveCategory] = useState<string | null>(
		categoryId || null
	);
	const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null);
	const [isContentLoading, setIsContentLoading] = useState(false); // Track content loading state

	// Use React Query to fetch album details when an albumId is present
	const {
		data: albumData,
		isLoading: isAlbumLoading,
		error: albumError,
	} = useFetch<{ name: string; isProvisional?: boolean; schoolYear?: string; unitat?: string }>({
		queryKey: ['album', albumId],
		url:
			albumId && albumId !== 'tots'
				? `/api/galeria/albums/${albumId}`
				: '', // Only fetch if albumId is valid
	});

	// Derive album name from fetch or set a default for "tots"
	const selectedAlbumName = useMemo(() => {
		if (albumId === 'tots') return 'Totes les fotos';
		if (isAlbumLoading) return 'Carregant...';
		if (albumError) return 'Error';
		return albumData?.name || '';
	}, [albumId, albumData, isAlbumLoading, albumError]);

	const [isEditingPhotos, setIsEditingPhotos] = useState(false); // State for photo edit mode
	const [pendingChanges, setPendingChanges] = useState<PendingChangesMap>(
		new Map()
	); // State for pending changes
	const [isConfirming, setIsConfirming] = useState(false); // State for confirmation loading
	const [confirmProgress, setConfirmProgress] = useState<{ current: number; total: number } | null>(null); // Progress during confirmation
	const [showClassificarDialog, setShowClassificarDialog] = useState(false); // Classificar dialog visibility
	const [showYearSuggestDialog, setShowYearSuggestDialog] = useState(false); // Year-suggest dialog visibility
	const [confirmError, setConfirmError] = useState<string | null>(null); // State for confirmation errors

	// State to track which albums are showing all photos (not just secret)
	const [albumShowAllPhotos, setAlbumShowAllPhotos] = useState<Set<string>>(new Set());

	// Toggle function for per-album "show all photos" mode
	const toggleAlbumShowAllPhotos = useCallback((albumId: string) => {
		setAlbumShowAllPhotos(prev => {
			const next = new Set(prev);
			if (next.has(albumId)) {
				next.delete(albumId);
			} else {
				next.add(albumId);
			}
			return next;
		});
	}, []);

	// Handler for secret album selection (includes "Totes les fotos privades")
	const handleSecretAlbumSelect = useCallback((albumId: string, _albumName?: string) => {
		setSelectedAlbum(albumId);
		setAlbumShowAllPhotos(prev => {
			const next = new Set(prev);
			next.delete(albumId);
			return next;
		});
	}, []);

	// Filters - using slider indices
	const [minYear, setMinYear] = useState<string>('');
	const [maxYear, setMaxYear] = useState<string>('');

	// Helper functions to convert between year string and slider index
	const yearToIndex = useCallback((year: string): number => {
		if (!year) return -1;
		return schoolYearsForSlider.indexOf(year);
	}, []);

	const indexToYear = useCallback((index: number): string => {
		if (index < 0 || index >= schoolYearsForSlider.length) return '';
		return schoolYearsForSlider[index];
	}, []);

	// Slider range state (derived from minYear/maxYear)
	const sliderValue = useMemo((): [number, number] => {
		const minIndex = minYear ? yearToIndex(minYear) : 0;
		const maxIndex = maxYear
			? yearToIndex(maxYear)
			: schoolYearsForSlider.length - 1;
		return [
			minIndex >= 0 ? minIndex : 0,
			maxIndex >= 0 ? maxIndex : schoolYearsForSlider.length - 1,
		];
	}, [minYear, maxYear, yearToIndex]);

	// Handler for slider changes
	const handleSliderChange = useCallback(
		(value: number | number[]) => {
			if (Array.isArray(value)) {
				const [newMinIndex, newMaxIndex] = value;
				// If slider is at extreme positions, use empty string (all)
				const newMinYear =
					newMinIndex === 0 ? '' : indexToYear(newMinIndex);
				const newMaxYear =
					newMaxIndex === schoolYearsForSlider.length - 1
						? ''
						: indexToYear(newMaxIndex);
				setMinYear(newMinYear);
				setMaxYear(newMaxYear);
				setAlbumGridPage(1);
				setSearchParams((prev) => {
					const newParams = new URLSearchParams(prev);
					newParams.set('page', '1');
					return newParams;
				});
			}
		},
		[indexToYear, setSearchParams]
	);

	// Get display text for the slider range
	const sliderDisplayText = useMemo((): string => {
		const startYear = minYear || schoolYearsForSlider[0];
		const endYear =
			maxYear || schoolYearsForSlider[schoolYearsForSlider.length - 1];
		if (!minYear && !maxYear) {
			return 'Tots els cursos';
		}
		return `${startYear} — ${endYear}`;
	}, [minYear, maxYear]);

	// State for Pictures pagination (when album is selected)
	const [picturesPage, setPicturesPage] = useState(1);
	const [picturesPageCount, setPicturesPageCount] = useState(1);
	const [picturesPageSize, setPicturesPageSize] = useState(100);
	const [picturesSortField, setPicturesSortField] = useState('upload_date'); // Add state for sort field
	const [picturesSortDirection, setPicturesSortDirection] = useState('desc'); // Add state for sort direction

	// Handler for Pictures pagination (memoized)
	const handlePicturesPageChange = useCallback((newPage: number) => {
		setPicturesPage(newPage);
	}, []);

	const handlePicturesPageSizeChange = useCallback((newSize: number) => {
		setPicturesPageSize(newSize);
		setPicturesPage(1); // Reset to first page when size changes
	}, []);

	// Handlers for Pictures sorting (memoized)
	const handlePicturesSortFieldChange = useCallback((newField: string) => {
		setPicturesSortField(newField);
		setPicturesPage(1); // Reset to first page when sort changes
	}, []);

	const handlePicturesSortDirectionChange = useCallback(
		(newDirection: string) => {
			setPicturesSortDirection(newDirection);
			setPicturesPage(1); // Reset to first page when sort changes
		},
		[]
	);

	// State for AlbumGrid pagination (when no album is selected)
	// Initialize from URL param if present
	const [albumGridPage, setAlbumGridPage] = useState(() => {
		const pageParam = searchParams.get('page');
		return pageParam ? parseInt(pageParam, 10) : 1;
	});
	const [albumGridPageCount, setAlbumGridPageCount] = useState(1);
	const [albumGridPageSize, setAlbumGridPageSize] = useState(33);
	const [albumGridSortField, setAlbumGridSortField] = useState('schoolYear'); // Default to schoolYear
	const [albumGridSortDirection, setAlbumGridSortDirection] =
		useState('desc');
	const [albumGridSeed, setAlbumGridSeed] = useState(Math.random()); // Seed for random sort

	// Handlers for AlbumGrid pagination and sorting
	const handleAlbumGridPageChange = useCallback((newPage: number) => {
		setAlbumGridPage(newPage);
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev);
			newParams.set('page', newPage.toString());
			return newParams;
		});
	}, [setSearchParams]);

	const handleAlbumGridPageSizeChange = useCallback((newSize: number) => {
		setAlbumGridPageSize(newSize);
		setAlbumGridPage(1);
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev);
			newParams.set('page', '1');
			return newParams;
		});
	}, [setSearchParams]);

	const handleAlbumGridSortFieldChange = useCallback((newField: string) => {
		if (newField === 'random') {
			const newSeed = Math.random();
			setAlbumGridSeed(newSeed);
			localStorage.setItem('album_grid_seed', newSeed.toString());
		}
		setAlbumGridSortField(newField);
		setAlbumGridPage(1);
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev);
			newParams.set('page', '1');
			return newParams;
		});
	}, [setSearchParams]);

	const handleAlbumGridSortDirectionChange = useCallback(
		(newDirection: string) => {
			setAlbumGridSortDirection(newDirection);
			setAlbumGridPage(1);
			setSearchParams((prev) => {
				const newParams = new URLSearchParams(prev);
				newParams.set('page', '1');
				return newParams;
			});
		},
		[setSearchParams]
	);

	// Effect to load seed from localStorage on mount
	useEffect(() => {
		const seedFromLocalStorage = localStorage.getItem('album_grid_seed');
		if (seedFromLocalStorage) {
			setAlbumGridSeed(parseFloat(seedFromLocalStorage));
		}
	}, []);

	useEffect(() => {
		// Set active category based on URL params
		if (categoryId) {
			setActiveCategory(categoryId);
			// Only set page from URL if we are in a category view AND not in an album view (logic handled by useState init, but sync if needed)
			const pageParam = searchParams.get('page');
			if (pageParam && !albumId) {
				setAlbumGridPage(parseInt(pageParam, 10));
			} else if (!pageParam && !albumId) {
				setAlbumGridPage(1);
			}
		} else {
			setActiveCategory(null);
		}
		// Set selected album based on URL params
		if (albumId) {
			setSelectedAlbum(albumId);
		} else {
			setSelectedAlbum(null);
		}
	}, [categoryId, albumId, searchParams]);

	const handleCategoryChange = useCallback(
		(category: string) => {
			setActiveCategory(category);
			setSelectedAlbum(null);
			setAlbumGridPage(1); // Reset album grid page when category changes
			navigate(`/galeria/${category}?page=1`);
		},
		[navigate]
	); // Dependency: navigate

	const handleBackToCategories = useCallback(() => {
		setActiveCategory(null);
		navigate('/galeria');
	}, [navigate]);

	const handleAlbumSelect = useCallback(
		(albumId: string) => {
			// Handle the special case for album creation
			if (albumId === 'new') {
				navigate(`/galeria/albums/${activeCategory}/nou`);
				return;
			}

			if (albumId === 'tots') {
				// Navigate to "tots" album within the current category
				// We map this to albumId='tots' in the URL, handled by backend virtual album logic if needed
				// or just special frontend handling
				navigate(`/galeria/${activeCategory}/tots`);
				return;
			}
			if (albumId.startsWith('unitat:')) {
				// Handle unit-specific "all photos" selection
				const unitatName = albumId.replace('unitat:', '');
				navigate(`/galeria/${unitatName}/tots`);
				return;
			}

			// Regular album selection
			navigate(`/galeria/${activeCategory}/${albumId}`);
		},
		[navigate, activeCategory]
	); // Dependencies: navigate, activeCategory

	const handleBackToAlbums = useCallback(() => {
		setSelectedAlbum(null);
		setIsEditingPhotos(false); // Exit edit mode when going back
		setPendingChanges(new Map()); // Clear pending changes when going back
		setConfirmError(null); // Clear errors
		setPicturesPage(1); // Reset pictures page when going back
		setAlbumShowAllPhotos(new Set()); // Clear per-album view state
		// Navigate back to secret category grid or regular category grid
		navigate(activeCategory === 'secret'
			? '/galeria/secret'
			: activeCategory === 'classificar'
			? '/galeria/classificar'
			: `/galeria/${activeCategory}?page=${albumGridPage}`);
	}, [navigate, activeCategory, albumGridPage]); // Dependencies: navigate, activeCategory, albumGridPage

	// Callback for Pictures component to update pending changes - MOVED UP
	const handlePendingChange = useCallback(
		(itemId: string, change: PendingChange | null) => {
			setPendingChanges((prev) => {
				const newChanges = new Map(prev);
				if (change === null) {
					newChanges.delete(itemId); // Remove change if null is passed
				} else {
					newChanges.set(itemId, change);
				}
				return newChanges;
			});
		},
		[]
	); // No dependencies needed

	// --- Pagination Jump/FLIP Logic ---
	const contentWrapperRef = useRef<HTMLDivElement>(null);
	const paginationRef = useRef<HTMLDivElement>(null);
    
    const { isBottom, isStuck } = useStickyFlip({
        contentWrapperRef,
        stickyContainerRef: paginationRef,
        stickyTop: navHeight + 10,
        forceSticky: isContentLoading, // Force sticky when content is loading
    });

	// Called by ClassificarDialog after a successful classification: the album's
	// unitat/schoolYear changed, so every cached view that buckets albums can be
	// stale ('Per classificar' counts, unitat lists, previews).
	// ⚠ Hooks rule: MUST be defined before the early returns below.
	const handleAlbumClassified = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['albums'] });
		queryClient.invalidateQueries({ queryKey: ['album', selectedAlbum] });
		queryClient.invalidateQueries({ queryKey: ['classifications', selectedAlbum] });
		queryClient.invalidateQueries({ queryKey: ['classification-overview'] });
		queryClient.invalidateQueries({ queryKey: ['album-slideshow-thumbnails'] });
		queryClient.invalidateQueries({ queryKey: ['category-thumbnails'] });
	}, [queryClient, selectedAlbum]);


	if (isFetchingUnitats) {
		return (
			<FullPageLoader>
				<LoadingGif size="large" />
			</FullPageLoader>
		);
	}

	if (fetchUnitatsError) {
		return (
			<WideContentContainer>
				<ErrorMessage>
					Error: {extractErrorMessage(fetchUnitatsError)}
				</ErrorMessage>
			</WideContentContainer>
		);
	}

	// Determine if the current user can edit the selected album/category
	const canEdit =
		!!user &&
		!!selectedAlbum && // Can only edit if an album is selected
		selectedAlbum !== 'tots' && // Cannot edit "all photos" view
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') || // trusted curators: any unitat
			user.unitat === activeCategory);

	const canSeeSecret =
		!!user &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') ||
			user.roles.includes('Unitat'));

	const canSeeClassificar =
		!!user &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Unitat') ||
			user.roles.includes('Caps') ||
			user.roles.includes('Excaps'));

	// --- Query Keys for Invalidation ---
	// Define query keys needed for invalidation
	// NOTE: the all-pictures bucket in useMediaGallery is 'totes' (NOT 'tots') —
	// keys must match the shapes produced there or invalidation misses silently.
	const picturesQueryKeyBase = [
		'pictures',
		selectedAlbum ? `album${selectedAlbum}` : 'totes',
		picturesPage,
		picturesPageSize,
		picturesSortField,
		picturesSortDirection,
		// No seed here as it's part of the pictures hook state, invalidation should catch all picture queries
	];
	const albumQueryKey = ['album', selectedAlbum];
	const albumsListQueryKey = ['albums', activeCategory || 'tots']; // Use activeCategory

	// --- Edit Mode Handlers ---

	// Handler for the main "Edit/Confirm" button
	const handleToggleEditMode = async () => {
		if (isEditingPhotos) {
			// --- Confirm Changes ---
			if (pendingChanges.size === 0) {
				// No changes, just exit edit mode
				setIsEditingPhotos(false);
				setConfirmError(null);
				return;
			}

			if (
				!window.confirm(
					`Segur que vols aplicar ${pendingChanges.size} canvi(s)?`
				)
			) {
				return; // User cancelled confirmation
			}

			setIsConfirming(true);
			setConfirmError(null);
			const totalChanges = pendingChanges.size;
			let processedCount = 0;

			// Apply every pending change. A failure does NOT abort the batch:
			// remaining changes still apply so the album never ends up in a
			// partially-described limbo; the entries that failed stay pending
			// for a retry (see below).
			const failures: { itemId: string; message: string }[] = [];

			for (const [itemId, change] of pendingChanges.entries()) {
				processedCount++;
				setConfirmProgress({ current: processedCount, total: totalChanges });
				try {
					if (change.action === 'delete') {
						await axiosInstance.delete(
							`/api/galeria/gallery-items/${itemId}`
						);
					} else if (change.action === 'rotate') {
						// One call regardless of angle: the backend accepts a
						// `steps` quarter-turn count (1 = ±90, 2 = 180°), so a
						// 180° rotation never produced two sequential requests.
						const steps = change.value === 180 ? 2 : 1;
						const direction: 'left' | 'right' =
							(change.value as number) === 270 ? 'left' : 'right';
						await axiosInstance.patch(
							`/api/galeria/gallery-items/${itemId}/rotate`,
							{ direction, steps }
						);
					} else if (change.action === 'toggleSecret') {
						await axiosInstance.patch(
							`/api/galeria/gallery-items/${itemId}/toggle-secret`,
							{ isSecret: change.value }
						);
					}
				} catch (err: unknown) {
					console.error(
						`Failed to apply change for item ${itemId}:`,
						err
					);
					failures.push({
						itemId,
						message: extractErrorMessage(err),
					});
				}
			}

			setIsConfirming(false);
			setConfirmProgress(null);

			if (failures.length > 0) {
				setConfirmError(
					`Error aplicant canvis: ${failures.length} de ${totalChanges} han fallat (${failures[0].message}).`
				);
			}

			{
				// Refresh in ALL cases so the UI reflects the server truth.
				await queryClient.invalidateQueries({
					queryKey: picturesQueryKeyBase,
				});
				await queryClient.invalidateQueries({
					queryKey: ['pictures', 'totes'],
				});
				await queryClient.invalidateQueries({
					queryKey: albumQueryKey,
				});
				await queryClient.invalidateQueries({
					queryKey: albumsListQueryKey,
				});

				if (failures.length === 0) {
					setPendingChanges(new Map()); // All applied — clear + exit edit mode
					setIsEditingPhotos(false);
				} else {
					// Failed entries remain pending; the user stays in edit mode
					// to fix (or re-apply) them without redoing the whole batch.
					const remaining = new Map();
					for (const f of failures) {
						const change = pendingChanges.get(f.itemId);
						if (change) remaining.set(f.itemId, change);
					}
					setPendingChanges(remaining);
					setIsEditingPhotos(true);
				}
			}

		} else {
			// --- Enter Edit Mode ---
			setIsEditingPhotos(true);
			setPendingChanges(new Map()); // Clear any stale changes
			setConfirmError(null); // Clear errors
		}
	};

	// --- Render Logic ---

	// Guard: redirect Familiar/Excaps away from secret/classificar routes
	if (activeCategory === 'secret' && !canSeeSecret) {
		return <Navigate to="/galeria" replace />;
	}
	if (activeCategory === 'classificar' && !canSeeClassificar) {
		return <Navigate to="/galeria" replace />;
	}

	// 1. Category Selection View (Root /galeria)
	if (!activeCategory) {
		return (
			<WideContentContainer>
				<PageTitle>Galeria</PageTitle>
				<CategoryGrid
					onCategorySelect={handleCategoryChange}
					canSeeSecret={canSeeSecret}
					canSeeClassificar={canSeeClassificar}
				/>
			</WideContentContainer>
		);
	}

	// 2. Album Grid or Pictures View
	return (
		<WideContentContainer>
			{/* Title Row: Contains title and pagination */}
			<S.TitleRow>
				<S.PageTitleContainer>
					<PageTitle style={{ marginBottom: 0, paddingTop: 0 }}>
						{activeCategory === 'secret'
							? 'Només Caps'
							: activeCategory === 'classificar'
							? 'Per classificar'
							: activeCategory === 'agrupament'
							? 'Agrupament'
							: (unitats &&
									unitats.find(
										(u) =>
											u.abreviacio.toLowerCase() ===
											activeCategory
									)?.abreviacio) ||
							  activeCategory.charAt(0).toUpperCase() +
									activeCategory.slice(1)}
					</PageTitle>
					{selectedAlbum && (
						<S.AlbumTitle>{selectedAlbumName}</S.AlbumTitle>
					)}
				</S.PageTitleContainer>

				{/* Edit Buttons — shown at category level only for authorized users viewing an album */}
				{canEdit && (
					<S.EditButtonsContainer>
						{canEdit && (
							<Button
								as={Link} // Render as a Link
								to={`/galeria/albums/${selectedAlbum}/editar`} // Navigate to edit route
								state={{ category: activeCategory }} // remember origin category
								style={{ textDecoration: 'none' }} // Remove link underline
							>
								Editar àlbum
							</Button>
						)}
						<Button
							onClick={handleToggleEditMode}
							disabled={isConfirming} // Disable while confirming
						>
							{isConfirming
								? confirmProgress
									? `Guardant (${confirmProgress.current}/${confirmProgress.total})...`
									: 'Confirmant...'
								: isEditingPhotos
								? `Confirmar ${
														pendingChanges.size > 0
															? `(${pendingChanges.size}) `
															: ''
												  }canvis`
								: 'Editar fotos'}
						</Button>
						{/* Cancel Button - only visible when editing */}
						{isEditingPhotos && (
							<Button
								onClick={() => {
									setIsEditingPhotos(false);
									setPendingChanges(new Map()); // Clear changes on cancel
									setConfirmProgress(null);
									setConfirmError(null);
								}}
								disabled={isConfirming}
								style={{
									backgroundColor: '#f39c12', // Orange color for cancel
								}}
							>
								Cancel·lar
							</Button>
						)}
					</S.EditButtonsContainer>
				)}
			</S.TitleRow>

			{/* Sticky Pagination for album grid view */}
			{!selectedAlbum && (
				<StickyFlipPaginationContainer
					$navHeight={navHeight}
					$isBottom={isBottom}
                    $isStuck={isStuck}
					ref={paginationRef}
				>
					<Pagination
						currentPage={albumGridPage}
						pageCount={albumGridPageCount}
						pageSize={albumGridPageSize}
						sortField={albumGridSortField}
						sortDirection={albumGridSortDirection}
						onPageChange={handleAlbumGridPageChange}
						onPageSizeChange={handleAlbumGridPageSizeChange}
						onSortFieldChange={handleAlbumGridSortFieldChange}
						onSortDirectionChange={
							handleAlbumGridSortDirectionChange
						}
						sortFieldOptions={[
							{ value: 'schoolYear', label: 'Curs' },
							{
								value: 'upload_date',
								label: 'Data de pujada',
							},
							{ value: 'name', label: 'Títol' },
							{ value: 'random', label: 'Aleatori' },
						]}
						pageSizeOptions={[7, 15, 33]}
					/>
				</StickyFlipPaginationContainer>
			

					
)}

			{/* Conditional Separator - Show only when an album is selected */}
			{(selectedAlbum || activeCategory === 'secret' || activeCategory === 'classificar') && <S.Separator />}

			{/* Content Wrapper for Scroll Detection (Filters + Grid) */}
			{!selectedAlbum && (
				<ContentWrapper ref={contentWrapperRef}>
					{/* Filter Row - Only shown when no album is selected (AlbumGrid view) */}
					{activeCategory !== 'secret' && activeCategory !== 'classificar' && (
						<S.TabsAndFilterRow>
							{/* Back to Categories Button */}
							<S.DesktopBackButton onClick={handleBackToCategories} style={{ marginRight: 'auto', display: 'flex' }}>
								← Categories
							</S.DesktopBackButton>

							<S.YearFilterInline>
								<S.YearFilterLabel>Curs:</S.YearFilterLabel>
								<S.YearRangeDisplay>
									{sliderDisplayText}
								</S.YearRangeDisplay>
								<S.SliderContainerInline>
									<Slider
										range
										min={0}
										max={schoolYearsForSlider.length - 1}
										value={sliderValue}
										onChange={handleSliderChange}
										allowCross={false}
										pushable={0}
										styles={{
											track: {
												backgroundColor:
													'var(--color-accent, #007bff)',
												height: 4,
											},
											rail: {
												backgroundColor: '#e0e0e0',
												height: 4,
											},
											handle: {
												backgroundColor:
													'var(--color-accent, #007bff)',
												borderColor:
													'var(--color-accent, #007bff)',
												opacity: 1,
												width: 14,
												height: 14,
												marginTop: -5,
												boxShadow:
													'0 1px 3px rgba(0,0,0,0.15)',
											},
										}}
									/>
								</S.SliderContainerInline>
							</S.YearFilterInline>
						</S.TabsAndFilterRow>
					)}

					{/* Banner and actions for Per classificar category */}
					{activeCategory === 'classificar' && (
						<div style={{ marginBottom: '20px' }}>
							<div style={{ marginBottom: '12px' }}>
								<S.BackButton
									onClick={() => navigate('/galeria')}
									>
									← Tornar a categories
								</S.BackButton>
							</div>
							<div style={{
								backgroundColor: '#fff3cd',
								border: '1px solid #ffc107',
								borderRadius: '6px',
								padding: '16px 20px',
								color: '#856404',
								fontSize: '0.95rem',
								lineHeight: 1.5,
							}}>
								Aquests àlbums estan pendents de classificar. Obriu un àlbum i feu servir el botó <strong>Classificar!</strong> per indicar-ne la unitat i el curs.
							</div>

							{/* Review classifications button — Admin / Agrupament only */}
							{!!user &&
								(user.roles.includes('Admin') ||
									user.roles.includes('Agrupament')) && (
									<div style={{
										display: 'flex',
										justifyContent: 'center',
										marginTop: '16px',
									}}>
										<button
											onClick={() =>
												navigate(
													'/galeria/classificar/classificacions',
												)
											}
											style={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: '8px',
												padding:
													'12px 28px',
												fontSize:
													'1rem',
												fontWeight: 600,
												color: 'white',
												background:
													'#3498db',
												border: 'none',
												borderRadius:
													'8px',
												cursor: 'pointer',
												transition:
													'all 0.2s ease',
												boxShadow:
													'0 2px 6px rgba(52,152,219,0.3)',
											}}
											onMouseEnter={(
												e,
											) => {
												e.currentTarget.style.background =
													'#2980b9';
											}}
											onMouseLeave={(
												e,
											) => {
												e.currentTarget.style.background =
													'#3498db';
											}}
										>
											📊 Veure totes les classificacions rebudes
										</button>
										<button
											onClick={() => navigate('/galeria/anys')}
											style={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: '8px',
												padding: '12px 28px',
												fontSize: '1rem',
												fontWeight: 600,
												color: 'white',
												background: '#27ae60',
												border: 'none',
												borderRadius: '8px',
												cursor: 'pointer',
												marginLeft: '12px',
											}}
										>
											📅 Veure suggeriments de curs
										</button>
									</div>
								)}
						</div>
					)}

					{/* Pass AlbumGrid pagination state/handlers */}
					{activeCategory !== 'secret' && activeCategory !== 'classificar' ? (
										<AlbumGrid
											category={activeCategory}
											onAlbumSelect={handleAlbumSelect}
											page={albumGridPage}
											pageSize={albumGridPageSize}
											sortField={albumGridSortField}
											sortDirection={albumGridSortDirection}
											seed={albumGridSeed} // Pass the seed
											// onPageChange removed
											onPageCountChange={setAlbumGridPageCount} // Update album grid page count
											minYear={minYear} // Pass filters
											maxYear={maxYear}
											onIsLoadingChange={setIsContentLoading} // Update loading state
										/>
					) : activeCategory === 'classificar' ? (
						/* Classificar Category - Show AlbumGrid for provisional/unclassified albums */
						<AlbumGrid
							category="classificar"
							onAlbumSelect={handleAlbumSelect}
							page={albumGridPage}
							pageSize={albumGridPageSize}
							sortField={albumGridSortField}
							sortDirection={albumGridSortDirection}
							seed={albumGridSeed}
							onPageCountChange={setAlbumGridPageCount}
							minYear={minYear}
							maxYear={maxYear}
							onIsLoadingChange={setIsContentLoading}
						/>
					) : (
						/* Secret Category - Show AlbumGrid for albums with private photos */
						<AlbumGrid
							category="secret"
							onAlbumSelect={handleSecretAlbumSelect}
							page={albumGridPage}
							pageSize={albumGridPageSize}
							sortField={albumGridSortField}
							sortDirection={albumGridSortDirection}
							seed={albumGridSeed}
							onPageCountChange={setAlbumGridPageCount}
							minYear={minYear}
							maxYear={maxYear}
							onIsLoadingChange={setIsContentLoading}
							showAllPrivatePseudoAlbum={true}
						/>
					)}
				</ContentWrapper>
			)}

			{/* Secret Category with album selected */}
			{activeCategory === 'secret' && selectedAlbum && (
				<>
					<S.AlbumViewControls>
						<S.AlbumTitleRow>
							<S.AlbumTitleSection>
								<h2 style={{ margin: 0, padding: 0, fontSize: '1.75rem', color: 'var(--color-secondary, #34495e)', lineHeight: 1.3 }}>
									{selectedAlbum === 'all-private'
										? 'Totes les fotos privades'
										: selectedAlbumName}
								</h2>
							</S.AlbumTitleSection>
							<S.MobileBackButton onClick={handleBackToAlbums}>
								← {activeCategory === 'secret' ? 'Tornar a categories' : 'Tornar als àlbums'}
							</S.MobileBackButton>
						</S.AlbumTitleRow>
					</S.AlbumViewControls>

					{/* "Veure totes les fotos" button - only for real albums, not pseudo-album */}
					{selectedAlbum !== 'all-private' && (
						<div style={{ textAlign: 'center', marginBottom: '16px' }}>
							<S.ShowAllPhotosButton
								onClick={() => toggleAlbumShowAllPhotos(selectedAlbum)}
							>
								{albumShowAllPhotos.has(selectedAlbum)
									? "Veure només les fotos privades de l'àlbum"
									: 'Veure totes les fotos de l\'àlbum'}
							</S.ShowAllPhotosButton>
						</div>
					)}

					{/* Sticky Pagination */}
					<StickyFlipPaginationContainer
						$navHeight={navHeight}
						$isBottom={isBottom}
						$isStuck={isStuck}
						ref={paginationRef}
					>
						<S.IntegratedControlsContainer>
							<Pagination
								currentPage={picturesPage}
								pageCount={picturesPageCount}
								pageSize={picturesPageSize}
								sortField={picturesSortField}
								sortDirection={picturesSortDirection}
								onPageChange={handlePicturesPageChange}
								onPageSizeChange={handlePicturesPageSizeChange}
								onSortFieldChange={handlePicturesSortFieldChange}
								onSortDirectionChange={handlePicturesSortDirectionChange}
								sortFieldOptions={[
									{ value: 'upload_date', label: 'Data' },
									{ value: 'random', label: 'Aleatori' },
								]}
								pageSizeOptions={[25, 50, 100, 200, 400]}
							/>
							<S.DesktopBackButton onClick={handleBackToAlbums}>
								← Tornar als àlbums
							</S.DesktopBackButton>
						</S.IntegratedControlsContainer>
					</StickyFlipPaginationContainer>

					<ContentWrapper ref={contentWrapperRef}>
						<Pictures
							albumId={selectedAlbum === 'all-private' ? undefined : selectedAlbum}
							unitat={selectedAlbum === 'all-private' ? undefined : activeCategory}
							page={picturesPage}
							pageSize={picturesPageSize}
							sortField={picturesSortField}
							sortDirection={picturesSortDirection}
							minYear={minYear || undefined}
							maxYear={maxYear || undefined}
							onPageChange={handlePicturesPageChange}
							onPageCountChange={setPicturesPageCount}
							onSortFieldChange={handlePicturesSortFieldChange}
							onSortDirectionChange={handlePicturesSortDirectionChange}
							canEdit={selectedAlbum !== 'all-private'}
							isEditingPhotos={selectedAlbum !== 'all-private' && isEditingPhotos}
							pendingChanges={pendingChanges}
							onPendingChange={handlePendingChange}
							onIsLoadingChange={setIsContentLoading}
							showSecret={albumShowAllPhotos.has(selectedAlbum) ? false : true}
							includeSecret={albumShowAllPhotos.has(selectedAlbum) ? true : undefined}
							hideAlbumName={false}
						/>
					</ContentWrapper>
				</>
			)}

			{/* Non-secret album view — guarded to not conflict with secret album view */}
			{activeCategory !== 'secret' && selectedAlbum && (
				<>
					<S.AlbumViewControls>
						{/* First row: Album title and back button */}
						<S.AlbumTitleRow>
							{/* Back button next to title for mobile/narrow views */}
							<S.MobileBackButton onClick={handleBackToAlbums}>
								← Tornar als àlbums
							</S.MobileBackButton>
						</S.AlbumTitleRow>

						{/* Display confirmation error if any */}
						{confirmError && (
							<ErrorMessage>{confirmError}</ErrorMessage>
						)}
					</S.AlbumViewControls>

					{/* Second row: Pagination with integrated back button - Sticky */}
					<StickyFlipPaginationContainer
						$navHeight={navHeight}
						$isBottom={isBottom}
                        $isStuck={isStuck}
						ref={paginationRef}
					>
						<S.IntegratedControlsContainer>
							{/* Pagination for Pictures */}
							<Pagination
								currentPage={picturesPage}
								pageCount={picturesPageCount}
								pageSize={picturesPageSize}
								sortField={picturesSortField}
								sortDirection={picturesSortDirection}
								onPageChange={handlePicturesPageChange}
								onPageSizeChange={handlePicturesPageSizeChange}
								onSortFieldChange={
									handlePicturesSortFieldChange
								}
								onSortDirectionChange={
									handlePicturesSortDirectionChange
								}
								sortFieldOptions={[
									{ value: 'upload_date', label: 'Data' },
									{ value: 'random', label: 'Aleatori' },
								]}
								pageSizeOptions={[25, 50, 100, 200, 400]} // Allow changing page size
							/>

							{/* Integrated Back button - hidden on mobile */}
							<S.DesktopBackButton onClick={handleBackToAlbums}>
								← Tornar als àlbums
							</S.DesktopBackButton>
						</S.IntegratedControlsContainer>
					</StickyFlipPaginationContainer>

					{/* Classification UI for provisional albums */}
					{albumData?.isProvisional && selectedAlbum && selectedAlbum !== 'tots' && (
						<>
							{/* Caps / Excaps — Classificar button */}
							{(user?.roles.includes('Caps') || user?.roles.includes('Excaps')) && (
								<div style={{ textAlign: 'center', marginBottom: '16px' }}>
									<S.ClassifyButton onClick={() => setShowClassificarDialog(true)}>
										Classificar!
									</S.ClassifyButton>
								</div>
							)}

							{/* Admin / Agrupament — View classifications */}
							{(user?.roles.includes('Admin') || user?.roles.includes('Agrupament')) && (
								<>
									<div style={{ textAlign: 'center', marginBottom: '12px' }}>
										<S.ClassifyButton onClick={() => setShowClassificarDialog(true)}>
											Classificar!
										</S.ClassifyButton>
									</div>
									<ClassificationResults albumId={selectedAlbum} />
								</>
							)}

							{showClassificarDialog && (
								<ClassificarDialog
									albumId={selectedAlbum}
									albumName={selectedAlbumName}
									onClassified={handleAlbumClassified}
									onClose={() => setShowClassificarDialog(false)}
								/>
							)}
						</>
					)}


					{/* Year-suggestion UI for published albums with unknown school year.
					    Only shown AFTER albumData resolves — during load the
					    !schoolYear condition is vacuously true and the orange button
					    would flash on every album before disappearing for most
					    (visible especially on mobile). */}
					{selectedAlbum &&
						selectedAlbum !== 'tots' &&
						!isAlbumLoading &&
						!albumError &&
						albumData !== undefined &&
						!albumData?.isProvisional &&
						!albumData?.schoolYear && (
						<>
							<div style={{ textAlign: 'center', margin: '4px 0 16px' }}>
								<S.ClassifyButton onClick={() => setShowYearSuggestDialog(true)}>
									📅 Saps de quin any és aquest àlbum?
								</S.ClassifyButton>
							</div>
							{showYearSuggestDialog && (
								<YearSuggestDialog
									albumId={selectedAlbum}
									albumName={selectedAlbumName}
									onClose={() => setShowYearSuggestDialog(false)}
								/>
							)}
						</>
					)}

					{/* Pictures Content */}
					<ContentWrapper ref={contentWrapperRef}>
						<Pictures
							albumId={
								selectedAlbum === 'tots'
									? undefined
									: selectedAlbum
							}
							unitat={
								selectedAlbum === 'tots' &&
								activeCategory !== 'tots'
									? activeCategory
									: undefined
							}
							page={picturesPage}
							pageSize={picturesPageSize}
							sortField={picturesSortField} // Pass sort state
							sortDirection={picturesSortDirection} // Pass sort state
							minYear={minYear || undefined}
							maxYear={maxYear || undefined}
							onPageChange={handlePicturesPageChange}
							onPageCountChange={setPicturesPageCount} // Callback for page count changes within Pictures
							onSortFieldChange={handlePicturesSortFieldChange} // Pass sort handlers
							onSortDirectionChange={
								handlePicturesSortDirectionChange
							} // Pass sort handlers
							// Pass down edit state, authorization, pending changes, and handler
							canEdit={canEdit}
							isEditingPhotos={isEditingPhotos}
							pendingChanges={pendingChanges}
							onPendingChange={handlePendingChange}
							// setIsEditingPhotos={setIsEditingPhotos} // Removed
							onIsLoadingChange={setIsContentLoading} // Update loading state
							showSecret={activeCategory === 'secret'} // Use activeCategory to determine if in secret mode
							hideAlbumName={!!selectedAlbum && selectedAlbum !== 'tots'} // Pass hideAlbumName
						/>
					</ContentWrapper>
				</>
			)}
		</WideContentContainer>
	);
};

export default GalleryContainer;